import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "urlInput",
    "loading",
    "error",
    "preview",
    "previewImage",
    "previewName",
    "previewPrice",
    "previewRetailer",
    "nameInput",
    "descriptionInput",
    "priceInput",
    "retailerInput",
    "imageInput"
  ]

  connect() {
    this.debounceTimer = null
  }

  disconnect() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
  }

  urlChanged() {
    const url = this.urlInputTarget.value.trim()

    // Clear previous timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }

    // Hide all states
    this.hideAllStates()

    // Don't scrape if URL is too short or invalid
    if (!url || url.length < 10 || !this.isValidUrl(url)) {
      return
    }

    // Debounce the scraping
    this.debounceTimer = setTimeout(() => {
      this.scrapeUrl(url)
    }, 500)
  }

  async scrapeUrl(url) {
    this.showLoading()

    // Cancel any previous request
    if (this.abortController) {
      this.abortController.abort()
    }

    this.abortController = new AbortController()
    const timeoutId = setTimeout(() => this.abortController.abort(), 15000) // 15 second timeout

    try {
      const response = await fetch(`/api/scrape_preview?url=${encodeURIComponent(url)}`, {
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        signal: this.abortController.signal
      })

      clearTimeout(timeoutId)

      const data = await response.json()

      if (data.error) {
        this.showError(data.error)
        return
      }

      this.populateFields(data)
      this.showPreview(data)
    } catch (error) {
      clearTimeout(timeoutId)
      if (error.name === "AbortError") {
        this.showError("Request timed out. The site may be slow. You can enter details manually.")
      } else {
        this.showError("Failed to fetch product details. You can enter them manually.")
      }
    }
  }

  populateFields(data) {
    // Only populate fields if they're empty or if this is a fresh scrape
    if (this.hasNameInputTarget && data.name && !this.nameInputTarget.value) {
      this.nameInputTarget.value = data.name
    }

    if (this.hasDescriptionInputTarget && data.description && !this.descriptionInputTarget.value) {
      this.descriptionInputTarget.value = data.description
    }

    if (this.hasPriceInputTarget && data.price && !this.priceInputTarget.value) {
      this.priceInputTarget.value = data.price
    }

    if (this.hasRetailerInputTarget && data.retailer && !this.retailerInputTarget.value) {
      this.retailerInputTarget.value = data.retailer
    }

    if (this.hasImageInputTarget && data.image_url && !this.imageInputTarget.value) {
      this.imageInputTarget.value = data.image_url
    }
  }

  showPreview(data) {
    this.hideAllStates()

    if (this.hasPreviewTarget) {
      this.previewTarget.classList.remove("hidden")
    }

    if (this.hasPreviewNameTarget && data.name) {
      this.previewNameTarget.textContent = data.name
    }

    if (this.hasPreviewPriceTarget && data.price) {
      this.previewPriceTarget.textContent = `$${parseFloat(data.price).toFixed(2)}`
    } else if (this.hasPreviewPriceTarget) {
      this.previewPriceTarget.textContent = ""
    }

    if (this.hasPreviewRetailerTarget) {
      this.previewRetailerTarget.textContent = data.retailer || ""
    }

    if (this.hasPreviewImageTarget && data.image_url) {
      this.previewImageTarget.src = data.image_url
      this.previewImageTarget.classList.remove("hidden")
    } else if (this.hasPreviewImageTarget) {
      this.previewImageTarget.classList.add("hidden")
    }
  }

  showLoading() {
    this.hideAllStates()
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove("hidden")
      this.loadingTarget.setAttribute("aria-live", "polite")
      this.loadingTarget.setAttribute("aria-busy", "true")
    }
  }

  showError(message) {
    this.hideAllStates()
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message
      this.errorTarget.classList.remove("hidden")
      this.errorTarget.setAttribute("role", "alert")
      this.errorTarget.setAttribute("aria-live", "assertive")
    }
  }

  hideAllStates() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add("hidden")
      this.loadingTarget.removeAttribute("aria-busy")
    }
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add("hidden")
    }
    if (this.hasPreviewTarget) {
      this.previewTarget.classList.add("hidden")
    }
  }

  isValidUrl(string) {
    try {
      const url = new URL(string)
      return url.protocol === "http:" || url.protocol === "https:"
    } catch (_) {
      return false
    }
  }

  csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }
}
